/**
 * tutorials.js — Contenido de la sección "Tutoriales".
 *
 * Sección independiente del Centro de Ayuda (Help.svelte / helpGuides.js).
 * Los tutoriales se agrupan por categoría y tienen un formato más rico:
 *
 *   TUTORIAL_CATEGORIES: [{ id, label, icon }]
 *   TUTORIALS: [{
 *     id,            // = id de vista del Dashboard (setView) → destino del CTA
 *     category,      // = id de una TUTORIAL_CATEGORIES
 *     title, icon, minutes, summary, intro,
 *     sections: [{ title, body, subs?: [{title, body}], tip? }],
 *     cta: { label, view }
 *   }]
 *
 * Todo el texto está en español (la app no usa i18n).
 */

export const TUTORIAL_CATEGORIES = [
	{ id: 'basics', label: 'Primeros pasos', icon: 'pie-chart' },
	{ id: 'crm', label: 'CRM y contactos', icon: 'kanban' },
	{ id: 'inbox', label: 'Conversaciones', icon: 'inbox' },
	{ id: 'ai', label: 'IA y automatización', icon: 'sparkles' },
	{ id: 'system', label: 'Configuración y equipo', icon: 'settings' }
];

export const TUTORIALS = [
	// ─────────────────────────── Primeros pasos ───────────────────────────
	{
		id: 'home',
		category: 'basics',
		title: 'Inicio y panel general',
		icon: 'pie-chart',
		minutes: 2,
		summary: 'Entiende de un vistazo el estado de tu operación desde la pantalla de Inicio.',
		intro: 'La pantalla de Inicio es tu resumen diario: conversaciones abiertas, leads, actividad reciente y los pasos que faltan para dejar tu cuenta lista.',
		sections: [
			{ title: 'Lee tus métricas', body: 'Arriba verás tarjetas con los números clave del día: conversaciones sin responder, leads nuevos y actividad reciente. Úsalas para saber por dónde empezar.' },
			{ title: 'Completa la puesta a punto', body: 'Si tu cuenta es nueva, verás una lista de tareas iniciales (conectar un canal, crear un flujo, invitar a tu equipo). Complétalas para aprovechar la plataforma al 100%.',
			  tip: 'Puedes ocultar la lista de puesta a punto cuando termines; no vuelve a aparecer.' },
			{ title: 'Salta a donde necesites', body: 'Desde las tarjetas y accesos directos puedes ir directo a la Bandeja, a Leads o a Canales sin buscar en el menú.' }
		],
		cta: { label: 'Ir a Inicio', view: 'home' }
	},
	{
		id: 'channels',
		category: 'basics',
		title: 'Conectar canales',
		icon: 'globe',
		minutes: 3,
		summary: 'Conecta WhatsApp y otros canales para empezar a recibir y enviar mensajes.',
		intro: 'Los canales son por dónde recibes y envías mensajes: WhatsApp (QR o API oficial), Facebook, Instagram, web chat, etc. Necesitas al menos uno conectado para empezar a conversar.',
		sections: [
			{ title: 'Abre Canales y crea uno', body: 'Haz clic en "Agregar canal" y elige el tipo. Para WhatsApp sin API oficial, elige "WhatsApp QR".' },
			{ title: 'Escanea el código QR', body: 'En WhatsApp QR, abre WhatsApp en tu teléfono → Dispositivos vinculados → Vincular dispositivo, y escanea el QR que aparece en pantalla.',
			  tip: 'Mantén el teléfono con batería y conexión: si se apaga por mucho tiempo, WhatsApp puede cerrar la sesión vinculada.' },
			{ title: 'Verifica la conexión', body: 'Cuando el estado pase a "Conectado", ya recibes y envías mensajes desde la Bandeja. Si se desconecta, vuelve a escanear el QR.' }
		],
		cta: { label: 'Ir a Canales', view: 'channels' }
	},
	{
		id: 'settings',
		category: 'basics',
		title: 'Ajustes de la cuenta',
		icon: 'settings',
		minutes: 2,
		summary: 'Personaliza tu perfil, tu organización y las preferencias de la app.',
		intro: 'En Ajustes configuras tus datos, los de tu organización y las preferencias generales de la plataforma.',
		sections: [
			{ title: 'Tu perfil', body: 'Actualiza tu nombre, correo y contraseña. Usa una contraseña segura, sobre todo si tu cuenta tiene permisos de administrador.' },
			{ title: 'Datos de la organización', body: 'Nombre del negocio, zona horaria y datos que aparecen en tus comunicaciones. La zona horaria afecta cómo se muestran fechas y horarios de atención.' },
			{ title: 'Preferencias', body: 'Ajusta tema (claro/oscuro) y notificaciones según cómo trabaje tu equipo.' }
		],
		cta: { label: 'Ir a Ajustes', view: 'settings' }
	},

	// ─────────────────────────── CRM y contactos ───────────────────────────
	{
		id: 'leads',
		category: 'crm',
		title: 'Leads y pipeline',
		icon: 'kanban',
		minutes: 4,
		summary: 'Sigue tus oportunidades por etapas en el tablero Kanban del CRM.',
		intro: 'El tablero de Leads (Kanban) te deja seguir tus oportunidades por etapas: Nuevo, Contactado, Calificado, Ganado, Perdido.',
		sections: [
			{ title: 'Crea o recibe leads', body: 'Agrégalos manualmente, desde una conversación, o automáticamente con un flujo de calificación.' },
			{ title: 'Arrástralos por etapas', body: 'Mueve cada tarjeta entre columnas según avanza el trato. El total y el valor de cada columna se actualizan solos.' },
			{ title: 'Filtra Activos / Todos', body: 'Por defecto ves los leads activos. Cambia a "Todos" para ver también los Ganados y Perdidos, cada uno con su badge de color.',
			  tip: 'Si "perdiste" un lead pero no aparece, revisa que el filtro esté en "Todos" y no en "Activos".' }
		],
		cta: { label: 'Ir a Leads', view: 'leads' }
	},
	{
		id: 'contacts',
		category: 'crm',
		title: 'Contactos',
		icon: 'users',
		minutes: 3,
		summary: 'Tu directorio central de personas: datos, historial y etiquetas.',
		intro: 'Los Contactos son el directorio de todas las personas con las que conversas. Cada contacto reúne sus datos, su historial de conversaciones y sus etiquetas.',
		sections: [
			{ title: 'De dónde salen los contactos', body: 'Se crean solos cuando alguien te escribe por cualquier canal, o puedes agregarlos a mano. Un mismo contacto agrupa todas sus conversaciones.' },
			{ title: 'Completa y edita datos', body: 'Nombre, teléfono, correo y atributos personalizados. Mientras más completo el contacto, más útil es en el CRM y en tus flujos con variables {{contact.name}}.' },
			{ title: 'Etiqueta y organiza', body: 'Aplica etiquetas al contacto para segmentar (cliente, proveedor, VIP, etc.). Las etiquetas viven en el contacto, así que lo acompañan en todas sus conversaciones.',
			  tip: 'Si ves dos contactos que parecen la misma persona, suele ser por un número escrito distinto: unifícalos para no perder historial.' }
		],
		cta: { label: 'Ir a Contactos', view: 'contacts' }
	},
	{
		id: 'labels',
		category: 'crm',
		title: 'Etiquetas',
		icon: 'tag',
		minutes: 2,
		summary: 'Clasifica contactos y conversaciones con etiquetas de colores.',
		intro: 'Las etiquetas te dejan clasificar y filtrar rápido. Se aplican al contacto, de modo que describen a la persona en toda la plataforma.',
		sections: [
			{ title: 'Crea tus etiquetas', body: 'Define un nombre y un color para cada una (ej. "Lead", "Soporte", "VIP", "Cliente"). Mantén un set corto y claro para que todo el equipo las use igual.' },
			{ title: 'Aplícalas', body: 'Desde la Bandeja o desde el Contacto, asigna una o varias etiquetas. Puedes usarlas también como condición dentro de un flujo (nodo de condición por etiqueta).' },
			{ title: 'Filtra con ellas', body: 'Usa las etiquetas para filtrar la Bandeja y los Contactos, y para segmentar campañas.',
			  tip: 'Las etiquetas se guardan en el contacto (no separadas por conversación), así que no necesitas re-etiquetar cada chat.' }
		],
		cta: { label: 'Ir a Etiquetas', view: 'labels' }
	},

	// ─────────────────────────── Conversaciones ───────────────────────────
	{
		id: 'conversations',
		category: 'inbox',
		title: 'La Bandeja de entrada',
		icon: 'inbox',
		minutes: 4,
		summary: 'Atiende todas las conversaciones de todos los canales en un solo lugar.',
		intro: 'La Bandeja es donde tú y tu equipo atienden las conversaciones de todos los canales en un solo lugar.',
		sections: [
			{ title: 'Responde conversaciones', body: 'Selecciona una conversación y escribe. Puedes enviar texto, imágenes y archivos adjuntos.',
			  tip: 'Si un adjunto grande no se envía, puede ser por el tamaño del archivo: reduce su peso e inténtalo de nuevo.' },
			{ title: 'Asigna y etiqueta', body: 'Asigna la conversación a un agente y usa etiquetas para organizarla (ej. "Lead", "Soporte", "VIP").' },
			{ title: 'Crea un lead desde el chat', body: 'Desde el panel del chat puedes convertir un contacto en lead del CRM y moverlo por las etapas del pipeline.' },
			{ title: 'Toma el control del bot', body: 'Si un flujo o agente IA está respondiendo, puedes intervenir en cualquier momento (handoff) para atender tú mismo.' }
		],
		cta: { label: 'Ir a la Bandeja', view: 'conversations' }
	},

	// ─────────────────────────── IA y automatización ───────────────────────────
	{
		id: 'flows',
		category: 'ai',
		title: 'Flujos (Salesbot)',
		icon: 'zap',
		minutes: 6,
		summary: 'Automatiza conversaciones paso a paso: bienvenida, menús y calificación.',
		intro: 'Los flujos son conversaciones automáticas: una secuencia de pasos (enviar mensaje, preguntar, decidir, escalar a un humano) que se ejecuta sola cuando llega un mensaje. Ideal para dar la bienvenida, mostrar un menú o calificar leads sin estar pendiente.',
		sections: [
			{ title: 'Empieza con una plantilla', body: 'En la pantalla de Flujos verás "Plantillas para empezar". Haz clic en "Usar" en cualquiera (Bienvenida, Menú de opciones, Calificación de leads, Fuera de horario) y se creará un flujo de ejemplo que puedes editar a tu gusto.' },
			{ title: 'Conoce las 3 áreas del editor', body: 'A la izquierda la Paleta (el trigger y los nodos que puedes agregar), al centro el Lienzo (donde conectas los pasos) y a la derecha el Inspector (donde editas el paso seleccionado).' },
			{ title: 'Elige cuándo se dispara (trigger)', body: 'Conversación nueva (al primer mensaje del cliente), Cada mensaje, o Palabra clave (cuando el cliente escribe algo como "precio"). Lo configuras en la Paleta.' },
			{ title: 'Conecta los pasos', body: 'Arrastra desde el punto de salida de un nodo hacia el siguiente. En un Menú o una Condición, conecta cada salida (cada opción / sí / no) a su paso correspondiente.',
			  tip: 'Un paso sin conectar deja al cliente "colgado". En los menús, conecta también la salida "otro" (respuesta no válida) para que nadie quede sin respuesta.' },
			{ title: 'Usa variables', body: 'Lo que el cliente responde en un paso "Pregunta" se guarda en una variable. Reutilízala escribiendo {{nombre}} en mensajes siguientes. También {{contact.name}} o {{contact.phone}}.' },
			{ title: 'Menú que reaparece y palabra "menú"', body: 'Un menú puede reaparecer solo si el cliente deja de responder (configúralo en el nodo), y la palabra "menú" o "inicio" devuelve al cliente al principio del flujo. Úsalas para no dejar a nadie atrapado.' },
			{ title: 'Pruébalo antes de activar', body: 'Usa el panel de prueba del editor: escribe respuestas como lo haría un cliente y verás qué camino toma el flujo. Así detectas opciones mal conectadas.' },
			{ title: 'Borrador vs Activo', body: 'Mientras está en BORRADOR, el flujo NO se ejecuta con clientes reales: solo lo editas y pruebas. Cuando esté listo, ponlo en ACTIVO con el interruptor de arriba. Solo se activa si no tiene errores.' }
		],
		cta: { label: 'Ir a Flujos', view: 'flows' }
	},
	{
		id: 'ai-agents',
		category: 'ai',
		title: 'Agentes IA',
		icon: 'sparkles',
		minutes: 4,
		summary: 'Deja que la IA responda con lenguaje natural usando tu conocimiento.',
		intro: 'Un Agente IA responde por ti con lenguaje natural, usando tu base de conocimiento. Úsalo para atender 24/7 y dentro de tus flujos (nodo "AI Agent").',
		sections: [
			{ title: 'Crea un agente', body: 'En Agentes IA, define su nombre, personalidad (vendedor, soporte...) e instrucciones de cómo debe responder.' },
			{ title: 'Dale conocimiento', body: 'Conecta tu Base de Conocimiento para que responda con info real de tu negocio (precios, horarios, productos).',
			  tip: 'Un agente sin conocimiento inventa o responde genérico. Dale contenido concreto para que sea útil.' },
			{ title: 'Actívalo en un canal o flujo', body: 'Puedes activar respuestas automáticas en un canal, o agregar un nodo "AI Agent" dentro de un flujo para un turno puntual.' }
		],
		cta: { label: 'Ir a Agentes IA', view: 'ai-agents' }
	},
	{
		id: 'knowledge',
		category: 'ai',
		title: 'Base de conocimiento',
		icon: 'book-open',
		minutes: 3,
		summary: 'El cerebro de tu IA: la información real de tu negocio.',
		intro: 'La Base de Conocimiento es la fuente de información que tus Agentes IA usan para responder: precios, horarios, productos, políticas y preguntas frecuentes.',
		sections: [
			{ title: 'Agrega contenido', body: 'Sube o escribe la información de tu negocio en piezas claras y concretas. Cuanto mejor redactado, mejores respuestas dará la IA.' },
			{ title: 'Mantenlo al día', body: 'Actualiza precios y datos cuando cambien. La IA responderá con lo que esté escrito aquí, así que un dato viejo se convierte en una respuesta equivocada.' },
			{ title: 'Conéctalo a tus agentes', body: 'Asocia la base de conocimiento a los Agentes IA que deban usarla. Sin esa conexión, el agente no ve el contenido.',
			  tip: 'Divide temas grandes en entradas separadas (una para precios, otra para horarios): la IA encuentra mejor lo específico.' }
		],
		cta: { label: 'Ir a Conocimiento', view: 'knowledge' }
	},
	{
		id: 'chatbots',
		category: 'ai',
		title: 'Chatbots',
		icon: 'bot',
		minutes: 3,
		summary: 'Configura respuestas automáticas por canal.',
		intro: 'Los Chatbots te permiten activar respuestas automáticas en tus canales, combinando flujos y agentes IA según cómo quieras atender.',
		sections: [
			{ title: 'Define el comportamiento', body: 'Elige qué responde automáticamente y en qué canal. Puedes apoyarte en un Agente IA para responder con lenguaje natural.' },
			{ title: 'Combina con flujos', body: 'Un chatbot puede iniciar un flujo estructurado (menús, calificación) o dejar que la IA converse libremente, según el caso.' },
			{ title: 'Prueba antes de publicar', body: 'Simula una conversación para confirmar que responde como esperas antes de dejarlo atendiendo clientes reales.',
			  tip: 'Deja siempre una salida a un humano: los clientes valoran poder hablar con una persona cuando el bot no resuelve.' }
		],
		cta: { label: 'Ir a Chatbots', view: 'chatbots' }
	},
	{
		id: 'campaigns',
		category: 'ai',
		title: 'Campañas de WhatsApp',
		icon: 'send',
		minutes: 4,
		summary: 'Envía mensajes masivos a segmentos de tus contactos.',
		intro: 'Las Campañas te dejan enviar un mensaje a muchos contactos a la vez: promociones, recordatorios o anuncios, dirigidos a un segmento.',
		sections: [
			{ title: 'Elige a quién', body: 'Selecciona el segmento de contactos, normalmente filtrando por etiquetas (ej. "Clientes", "Interesados"). Envía solo a quien corresponda.' },
			{ title: 'Redacta el mensaje', body: 'Escribe un mensaje claro y con un llamado a la acción. Puedes personalizarlo con variables como el nombre del contacto.' },
			{ title: 'Revisa antes de enviar', body: 'Confirma el segmento y el texto. Un envío masivo no se puede "deshacer", así que revisa bien destinatarios y contenido.',
			  tip: 'Evita el spam: envía a quien realmente espera tus mensajes. Los envíos no solicitados pueden hacer que WhatsApp bloquee tu número.' }
		],
		cta: { label: 'Ir a Campañas', view: 'campaigns' }
	},
	{
		id: 'ai-providers',
		category: 'ai',
		title: 'Proveedores IA',
		icon: 'plug',
		minutes: 2,
		summary: 'Conecta las llaves de los servicios de IA que usará la plataforma.',
		intro: 'En Proveedores IA conectas las credenciales (API keys) de los servicios de inteligencia artificial que usarán tus agentes.',
		sections: [
			{ title: 'Agrega tu proveedor', body: 'Elige el proveedor y pega su API key. Sin una llave válida, los Agentes IA no pueden responder.' },
			{ title: 'Mantén tus llaves seguras', body: 'Las credenciales se guardan cifradas. Aun así, trátalas como contraseñas: no las compartas y rótalas si crees que se filtraron.',
			  tip: 'Si un Agente IA deja de responder de golpe, revisa aquí primero: suele ser una llave vencida o sin saldo.' },
			{ title: 'Verifica la conexión', body: 'Tras guardar, confirma que el estado del proveedor sea válido antes de depender de él en producción.' }
		],
		cta: { label: 'Ir a Proveedores IA', view: 'ai-providers' }
	},

	// ─────────────────────────── Configuración y equipo ───────────────────────────
	{
		id: 'team',
		category: 'system',
		title: 'Equipo',
		icon: 'users',
		minutes: 3,
		summary: 'Organiza a los agentes que atienden y cómo se reparten el trabajo.',
		intro: 'En Equipo gestionas a las personas que atienden conversaciones y cómo se organizan para responder a los clientes.',
		sections: [
			{ title: 'Agrega a tu equipo', body: 'Suma a los agentes que atenderán la Bandeja. Cada uno tendrá su propio acceso y podrá ser asignado a conversaciones.' },
			{ title: 'Asigna conversaciones', body: 'Reparte las conversaciones entre agentes para que quede claro quién atiende a quién y nada se quede sin respuesta.' },
			{ title: 'Trabajo en equipo', body: 'Usa asignaciones y menciones para coordinar. Un cliente bien atendido casi siempre es resultado de un reparto claro del trabajo.',
			  tip: '¿Necesitas crear usuarios o cambiar contraseñas? Eso se hace en la sección Usuarios (ver ese tutorial).' }
		],
		cta: { label: 'Ir a Equipo', view: 'team' }
	},
	{
		id: 'users',
		category: 'system',
		title: 'Usuarios y accesos',
		icon: 'user',
		minutes: 3,
		summary: 'Crea usuarios, define roles y gestiona contraseñas de tu organización.',
		intro: 'En Usuarios, los administradores de la organización gestionan quién tiene acceso a la plataforma, con qué rol y con qué contraseña.',
		sections: [
			{ title: 'Crea usuarios', body: 'Como administrador de tu organización, agrega nuevos usuarios con su correo y rol. Cada uno recibe su propio acceso.' },
			{ title: 'Roles y permisos', body: 'Asigna el rol adecuado (por ejemplo administrador o agente). El rol define qué secciones puede ver y qué acciones puede realizar cada persona.' },
			{ title: 'Cambia contraseñas', body: 'Puedes restablecer la contraseña de un usuario de tu organización cuando lo necesiten.',
			  tip: 'Da a cada persona solo el rol que necesita. Menos permisos de los estrictamente necesarios = una cuenta más segura.' }
		],
		cta: { label: 'Ir a Usuarios', view: 'users' }
	}
];
